// Room.jsx
import {
    useTracks,
    useRoomContext,
    useConnectionState,
    RoomAudioRenderer,
} from '@livekit/components-react';
import { Track, ConnectionQuality } from 'livekit-client';

import VideoGrid from './VideoGrid';
import ScreenShareView from './ScreenShareView';
import Controls from './Controls';
import TrackTile from './TrackTile';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { DataPacket_Kind, RoomEvent, ConnectionState } from 'livekit-client';
import Chat from './Chat';
import { ENDPOINTS } from '../endpoints';
import { useLiveKitSession } from '../LiveKitSessionContext';
import { authJsonHeaders } from '../telemetryApi';
import { captureException } from '../clientLogReporter';
import { useRecordingTimelineReporter } from '../useRecordingTimelineReporter';
import { isRenderableScreenShareTrack } from '../screenShareUtils';
import { playJoinBeep } from '../joinBeep';
import { pickSpeakerSinkId } from '../pickSpeakerSinkId';
import { isLikelyAndroidChrome, shouldTryApplyAudioSink } from '../audioOutputSupport';
import { getClientBrowserInfo } from '../clientBrowserInfo';
import { useIntegrityMonitor } from '../../integrity/useIntegrityMonitor';
import IntegrityConsent from '../../integrity/IntegrityConsent';
import { ROLE_CANDIDATE } from '../participantRole';
import { INTEGRITY_UI_ENABLED } from '../endpoints';
import { fetchAudioTranscriptionDisclosure } from '../observerDisclosure';
import './room.css';

/** Map ConnectionQuality enum to numeric level for network indicator (0-4) */
function connectionQualityToLevel(quality) {
    if (quality == null) return 0;
    const q = typeof quality === 'string' ? quality.toLowerCase() : String(quality);
    switch (q) {
        case 'excellent': case ConnectionQuality.Excellent: return 4;
        case 'good': case ConnectionQuality.Good: return 3;
        case 'poor': case ConnectionQuality.Poor: return 2;
        case 'lost': case ConnectionQuality.Lost: return 1;
        default: return 0;
    }
}

function isUserGestureRequiredSinkError(err) {
    const msg = String(err?.message || err || '').toLowerCase();
    return (
        msg.includes('user gesture') ||
        msg.includes('notallowederror') ||
        msg.includes('permission denied')
    );
}

function announceRecordingState(isRecording) {
    if (typeof window === 'undefined') return;
    const synth = window.speechSynthesis;
    if (!synth || typeof window.SpeechSynthesisUtterance !== 'function') return;
    try {
        const utterance = new window.SpeechSynthesisUtterance(
            isRecording ? 'Recording has started.' : 'Recording has stopped.'
        );
        utterance.rate = 1;
        utterance.pitch = 1;
        utterance.volume = 1;
        // Cancel queued announcements so the latest state is always spoken first.
        synth.cancel();
        synth.speak(utterance);
    } catch (_) {
        // Best-effort UX cue; ignore unsupported/runtime speech errors.
    }
}

/** Shared shape for /client/presence and /client/session audit APIs. */
function buildClientAuditPayload(room, event) {
    const lp = room?.localParticipant;
    if (!lp || !room?.name) return null;
    const meetingRoom = {
        sid: room.sid,
        name: room.name,
        metadata: room.metadata ?? null,
        numParticipants: room.numParticipants,
        connectionState: room.state,
        remoteParticipantCount: room.participants?.size ?? 0,
    };
    const perms = lp.permissions;
    const permissions = perms
        ? {
            canSubscribe: perms.canSubscribe,
            canPublish: perms.canPublish,
            canPublishData: perms.canPublishData,
            hidden: perms.hidden,
            recorder: perms.recorder,
            canUpdateMetadata: perms.canUpdateMetadata,
            agent: perms.agent,
            canPublishSources: (perms.canPublishSources || []).map(String),
        }
        : null;
    const screenPub = lp.getTrack(Track.Source.ScreenShare);
    const participant = {
        sid: lp.sid,
        identity: lp.identity,
        name: lp.name ?? null,
        metadata: lp.metadata ?? null,
        connectionQuality: lp.connectionQuality,
        isMicrophoneEnabled: lp.isMicrophoneEnabled,
        isCameraEnabled: lp.isCameraEnabled,
        isSpeaking: lp.isSpeaking,
        screenSharing: Boolean(screenPub?.track),
        publishedTrackCount: lp.tracks?.size ?? 0,
        permissions,
    };
    const client = {};
    if (typeof navigator !== 'undefined') {
        client.userAgent = navigator.userAgent || null;
        client.language = navigator.language || null;
        client.languages = navigator.languages ? [...navigator.languages] : null;
        client.platform = navigator.platform || null;
    }
    if (typeof document !== 'undefined') {
        client.visibilityState = document.visibilityState;
        client.hidden = document.hidden;
    }
    Object.assign(client, getClientBrowserInfo());
    const clientContext = Object.keys(client).length ? client : null;
    return {
        roomName: room.name,
        identity: lp.identity,
        event,
        ts: Date.now(),
        meetingRoom,
        participant,
        client: clientContext,
    };
}

export default function RoomView() {
    const room = useRoomContext();
    const connectionState = useConnectionState(room);
    const { telemetryJwt, integrityJwt, participantRole } = useLiveKitSession();
    const localIdentity = room?.localParticipant?.identity;
    const [activeSpeakerSid, setActiveSpeakerSid] = useState(null);
    const [pinnedSid, setPinnedSid] = useState(null);
    const [networkQuality, setNetworkQuality] = useState({});
    const [chatOpen, setChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [recording, setRecording] = useState(false);
    const [recordingStoppedMessage, setRecordingStoppedMessage] = useState(null);
    const [recordingStarterIdentity, setRecordingStarterIdentity] = useState(null);
    const [pendingStopApproval, setPendingStopApproval] = useState(null);
    const [isApprovalSubmitting, setIsApprovalSubmitting] = useState(false);
    const joinBeepPlayedRef = useRef(false);
    /** Auto-start recording at most once per room session (poll runs repeatedly). */
    const recordingAutoStartAttemptedRef = useRef(false);
    /** Dedupe GET /record/status lastRecordingError so reconnect polling does not spam the banner. */
    const lastRecordingErrorPollKeyRef = useRef(null);
    /** User override from dropdown; empty string = prefer auto loudspeaker (see autoSpeakerSinkId). */
    const [audioSinkId, setAudioSinkId] = useState('');
    const [autoSpeakerSinkId, setAutoSpeakerSinkId] = useState('');
    const [audioOutputDevices, setAudioOutputDevices] = useState([]);
    // Biometric consent decision: null = undecided (modal shown), true/false once chosen.
    const [integrityConsent, setIntegrityConsent] = useState(null);
    // TI-1111: whether the consent notice must disclose audio transcription — derived from
    // the SERVER observer flag (observer.lifelike.enabled), NOT a client build flag, so the
    // notice cannot drift from what the server actually transcribes. null = not yet known;
    // the modal is held until this resolves (≤3s, fail-closed to disclosed) so the recorded
    // notice version never changes under the candidate's eyes.
    const [audioDisclosure, setAudioDisclosure] = useState(null);
    useEffect(() => {
        if (!INTEGRITY_UI_ENABLED) return undefined;
        let alive = true;
        fetchAudioTranscriptionDisclosure().then((disclose) => {
            if (alive) setAudioDisclosure(disclose);
        });
        return () => {
            alive = false;
        };
    }, []);
    const sinkGestureBlockedRef = useRef(false);
    const recordingAnnouncementPrevRef = useRef(null);

    /** Client-side timeline: mute/cam/screen → Redis while recording (webhook workaround). */
    useRecordingTimelineReporter(room, recording);

    // One-time join beep when connected (may stay silent on iOS until user interacts)
    useEffect(() => {
        if (!room) return;
        const tryBeep = () => {
            if (joinBeepPlayedRef.current) return;
            joinBeepPlayedRef.current = true;
            playJoinBeep();
        };
        const onConnected = () => tryBeep();
        room.on(RoomEvent.Connected, onConnected);
        if (room.state === ConnectionState.Connected) {
            tryBeep();
        }
        return () => {
            room.off(RoomEvent.Connected, onConnected);
        };
    }, [room]);

    // Clear recording-stopped message after 5s
    useEffect(() => {
        if (!recordingStoppedMessage) return;
        const t = setTimeout(() => setRecordingStoppedMessage(null), 5000);
        return () => clearTimeout(t);
    }, [recordingStoppedMessage]);

    useEffect(() => {
        recordingAutoStartAttemptedRef.current = false;
        lastRecordingErrorPollKeyRef.current = null;
    }, [room?.name]);

    useEffect(() => {
        if (!room) return;
        const onDisc = () => {
            recordingAutoStartAttemptedRef.current = false;
        };
        room.on(RoomEvent.Disconnected, onDisc);
        return () => {
            room.off(RoomEvent.Disconnected, onDisc);
        };
    }, [room]);

    // Reset recording voice dedupe when user switches room.
    useEffect(() => {
        recordingAnnouncementPrevRef.current = null;
    }, [room?.name]);

    // Voice prompt for recording transitions (skip initial value from status sync).
    useEffect(() => {
        if (connectionState !== ConnectionState.Connected) return;
        const previous = recordingAnnouncementPrevRef.current;
        if (previous === null) {
            recordingAnnouncementPrevRef.current = recording;
            return;
        }
        if (previous === recording) return;
        recordingAnnouncementPrevRef.current = recording;
        announceRecordingState(recording);
    }, [recording, connectionState]);

    /**
     * One-shot server truth on connect/reconnect + auto-start when configured.
     * While in the room, recording on/off follows reliable data messages (recordingStatus)
     * from the starter; starter also rebroadcasts when a new participant joins so late joiners sync.
     */
    useEffect(() => {
        if (!room?.name || !telemetryJwt) return;
        if (connectionState !== ConnectionState.Connected) return;
        let cancelled = false;
        const url = `${ENDPOINTS.RECORD_STATUS}?roomName=${encodeURIComponent(room.name)}`;

        const poll = () => {
            fetch(url, { headers: authJsonHeaders(telemetryJwt) })
                .then((res) => (res.ok ? res.json() : { active: false }))
                .then((data) => {
                    if (cancelled || !data) return;
                    if (typeof data.active === 'boolean') {
                        setRecording(data.active);
                        setRecordingStarterIdentity(data.starterIdentity || null);
                    }
                    const errPayload = data?.lastRecordingError;
                    const errMsg =
                        errPayload && typeof errPayload.message === 'string'
                            ? errPayload.message
                            : null;
                    if (errMsg) {
                        const dedupeKey = `${errPayload.ts ?? ''}:${errMsg}`;
                        if (lastRecordingErrorPollKeyRef.current !== dedupeKey) {
                            lastRecordingErrorPollKeyRef.current = dedupeKey;
                            setRecording(false);
                            setRecordingStarterIdentity(null);
                            setRecordingStoppedMessage(errMsg);
                        }
                    }
                    if (
                        !cancelled &&
                        !recordingAutoStartAttemptedRef.current &&
                        data?.autoStartOnSessionStart === true &&
                        data?.active === false &&
                        room?.localParticipant?.identity
                    ) {
                        recordingAutoStartAttemptedRef.current = true;
                        fetch(ENDPOINTS.RECORD_START, {
                            method: 'POST',
                            headers: authJsonHeaders(telemetryJwt),
                            body: JSON.stringify({
                                roomName: room.name,
                                requestedBy: room.localParticipant.identity,
                            }),
                        })
                            .then((res) => res.text().then((text) => ({ ok: res.ok, text })))
                            .then(({ ok }) => {
                                if (cancelled) return;
                                if (!ok) return;
                                const starter = room.localParticipant.identity;
                                setRecording(true);
                                setRecordingStarterIdentity(starter);
                                setRecordingStoppedMessage(
                                    'Recording started automatically for this session.'
                                );
                                try {
                                    room.localParticipant.publishData(
                                        new TextEncoder().encode(
                                            JSON.stringify({
                                                type: 'recordingStatus',
                                                recording: true,
                                                starterIdentity: starter,
                                            })
                                        ),
                                        DataPacket_Kind.RELIABLE
                                    );
                                } catch (_) {}
                            })
                            .catch((err) => {
                                recordingAutoStartAttemptedRef.current = false;
                                captureException(err, 'record_auto_start');
                            });
                    }
                })
                .catch((err) => captureException(err, 'record_status_fetch'));
        };

        poll();
        const onReconnected = () => {
            if (!cancelled) poll();
        };
        room.on(RoomEvent.Reconnected, onReconnected);
        return () => {
            cancelled = true;
            room.off(RoomEvent.Reconnected, onReconnected);
        };
    }, [room, room?.name, room?.localParticipant?.identity, telemetryJwt, connectionState]);

    useEffect(() => {
        if (!room) return;
        if (!recording || !recordingStarterIdentity) return;
        const rebroadcast = () => {
            if (room.localParticipant?.identity !== recordingStarterIdentity) return;
            try {
                room.localParticipant.publishData(
                    new TextEncoder().encode(
                        JSON.stringify({
                            type: 'recordingStatus',
                            recording: true,
                            starterIdentity: recordingStarterIdentity,
                        })
                    ),
                    DataPacket_Kind.RELIABLE
                );
            } catch (_) { /* ignore */ }
        };
        room.on(RoomEvent.ParticipantConnected, rebroadcast);
        return () => {
            room.off(RoomEvent.ParticipantConnected, rebroadcast);
        };
    }, [room, recording, recordingStarterIdentity]);

    // 🔑 Collect ALL tracks (audio included)
    const tracks = useTracks([
        Track.Source.Camera,
        Track.Source.ScreenShare,
        Track.Source.Microphone,
    ]);

    const screenSharingSids = useMemo(() => {
        const s = new Set();
        for (const t of tracks) {
            if (
                t.source === Track.Source.ScreenShare &&
                t.participant?.sid &&
                isRenderableScreenShareTrack(t)
            ) {
                s.add(t.participant.sid);
            }
        }
        return s;
    }, [tracks]);

    // 🎯 Active screen share only (ignore ended track refs that still appear in useTracks)
    const screenTrack = useMemo(() => {
        const raw = tracks.find((t) => t.source === Track.Source.ScreenShare);
        return isRenderableScreenShareTrack(raw) ? raw : undefined;
    }, [tracks]);

    const hadLiveScreenShareRef = useRef(false);
    useEffect(() => {
        const live = Boolean(screenTrack);
        if (hadLiveScreenShareRef.current && !live) {
            setPinnedSid(null);
        }
        hadLiveScreenShareRef.current = live;
    }, [screenTrack]);

    //   const cameraTracks = tracks.filter(
    //     t => t.source === Track.Source.Camera
    //   );

    const localCameraTrack = tracks.find(
        t => t.source === Track.Source.Camera && t.participant.isLocal
    );

    // One entry per remote participant (with or without camera) so audio-only users get a tile
    const remoteParticipantsWithTracks = room
        ? Array.from(room.participants.values()).map((participant) => {
            const cameraTrack = tracks.find(
                (t) => t.participant.sid === participant.sid && t.source === Track.Source.Camera
            ) || null;
            return { participant, cameraTrack };
        })
        : [];

    const audioTracks = tracks.filter(
        t => t.source === Track.Source.Microphone
    );

    // Enumerate audio outputs and pick a speaker-like sink (mobile often defaults remote audio to earpiece).
    useEffect(() => {
        if (!room || room.state !== ConnectionState.Connected) return;
        if (!navigator.mediaDevices?.enumerateDevices) return;

        let cancelled = false;
        const run = () => {
            navigator.mediaDevices.enumerateDevices().then((list) => {
                if (cancelled) return;
                const outs = list.filter((d) => d.kind === 'audiooutput');
                setAudioOutputDevices(outs);
                const mobileHeuristic =
                    typeof window !== 'undefined' &&
                    window.matchMedia?.('(pointer: coarse)')?.matches === true;
                // Only auto-force sink on Android Chrome (earpiece issue). Other browsers
                // should stay on default output unless user explicitly chooses a sink.
                if (isLikelyAndroidChrome()) {
                    setAutoSpeakerSinkId(pickSpeakerSinkId(outs, { mobileHeuristic }));
                } else {
                    setAutoSpeakerSinkId('');
                }
            }).catch((err) => captureException(err, 'enumerate_audio_outputs'));
        };

        run();
        const t0 = window.setTimeout(run, 400);
        const t1 = window.setTimeout(run, 1500);
        const t2 = window.setTimeout(run, 4000);
        const iv = window.setInterval(run, 5000);
        navigator.mediaDevices.addEventListener('devicechange', run);
        return () => {
            cancelled = true;
            window.clearTimeout(t0);
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.clearInterval(iv);
            navigator.mediaDevices.removeEventListener('devicechange', run);
        };
    }, [room, room?.state]);

    const effectiveAudioSinkId = audioSinkId || autoSpeakerSinkId;

    // Route remote meeting audio (setSinkId: Android Chrome / some desktop; not iOS Safari).
    useEffect(() => {
        if (!room || !effectiveAudioSinkId) return;
        if (!shouldTryApplyAudioSink()) return;
        if (sinkGestureBlockedRef.current) return;

        const root = document.querySelector('.room-container');
        if (!root) return;

        const apply = () => {
            root.querySelectorAll('audio').forEach((el) => {
                if (sinkGestureBlockedRef.current) return;
                if (typeof el.setSinkId === 'function') {
                    try {
                        if (el.sinkId === effectiveAudioSinkId) return;
                        el.setSinkId(effectiveAudioSinkId).catch((err) => {
                            // Safari may require explicit user interaction before sink switch; avoid log spam loops.
                            if (isUserGestureRequiredSinkError(err)) {
                                sinkGestureBlockedRef.current = true;
                                return;
                            }
                            captureException(err, 'audio_set_sink_id');
                        });
                    } catch (e) {
                         captureException(e, 'audio_set_sink_id_sync');
                    }
                }
            });
        };
        apply();
        const mo = new MutationObserver(apply);
        mo.observe(root, { childList: true, subtree: true });
        const timeout = window.setTimeout(apply, 2000);
        return () => {
            mo.disconnect();
            window.clearTimeout(timeout);
        };
    }, [room, effectiveAudioSinkId, audioTracks.length]);

    useEffect(() => {
        sinkGestureBlockedRef.current = false;
    }, [effectiveAudioSinkId]);

    // Tab / window focus → room data channel + HTTP audit on token server (Bearer telemetry JWT).
    useEffect(() => {
        if (!room?.name || !localIdentity) return;
        if (connectionState !== ConnectionState.Connected) return;
        if (!telemetryJwt) return;

        const sendPresence = (event) => {
            const body = buildClientAuditPayload(room, event);
            if (!body) return;
            const payload = JSON.stringify({ type: 'clientPresence', ...body });
            try {
                room.localParticipant.publishData(
                    new TextEncoder().encode(payload),
                    DataPacket_Kind.RELIABLE
                );
            } catch (_) { /* ignore */ }
            fetch(ENDPOINTS.CLIENT_PRESENCE, {
                method: 'POST',
                headers: authJsonHeaders(telemetryJwt),
                body: JSON.stringify(body),
                keepalive: true,
            }).catch((err) => captureException(err, 'client_presence_post'));
        };

        const onVisibility = () => {
            sendPresence(document.hidden ? 'tab_hidden' : 'tab_visible');
        };

        // 🛡️ Fraud Prevention: Prevent Right-Click, Copy, and Paste
        const onContextMenu = (e) => {
            e.preventDefault();
            sendPresence('fraud_right_click_attempt');
        };

        const onCopy = (e) => {
            e.preventDefault();
            sendPresence('fraud_copy_attempt');
            // alert('Copying is disabled during the interview for security reasons.');
        };

        const onPaste = (e) => {
            // We only block paste on inputs/textareas to prevent cheating
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                e.preventDefault();
                e.stopPropagation();
                sendPresence('fraud_paste_attempt');
                alert('Pasting is disabled to ensure interview integrity.');
            }
        };

        // Alt+Tab to another app: visibilitychange usually fires; blur covers cases where it does not.
        const onWindowBlur = () => sendPresence('window_blur');
        const onWindowFocus = () => sendPresence('window_focus');

        document.addEventListener('visibilitychange', onVisibility);
        document.addEventListener('contextmenu', onContextMenu, true);
        document.addEventListener('copy', onCopy, true);
        document.addEventListener('paste', onPaste, true);
        window.addEventListener('blur', onWindowBlur);
        window.addEventListener('focus', onWindowFocus);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            document.removeEventListener('contextmenu', onContextMenu, true);
            document.removeEventListener('copy', onCopy, true);
            document.removeEventListener('paste', onPaste, true);
            window.removeEventListener('blur', onWindowBlur);
            window.removeEventListener('focus', onWindowFocus);
        };
    }, [room, room?.name, localIdentity, connectionState, telemetryJwt]);

    const sessionJoinSentRef = useRef(null);
    useEffect(() => {
        if (!room?.name || !localIdentity || !telemetryJwt) return;
        if (connectionState !== ConnectionState.Connected) return;
        const key = `${room.name}:${localIdentity}`;
        if (sessionJoinSentRef.current === key) return;
        const audit = buildClientAuditPayload(room, 'session_join');
        if (!audit) return;
        sessionJoinSentRef.current = key;
        const lp = room.localParticipant;
        fetch(ENDPOINTS.CLIENT_SESSION, {
            method: 'POST',
            headers: authJsonHeaders(telemetryJwt),
            body: JSON.stringify({
                roomName: audit.roomName,
                identity: audit.identity,
                ts: audit.ts,
                networkLevel: connectionQualityToLevel(lp.connectionQuality),
                participant: audit.participant,
                client: audit.client,
            }),
            keepalive: true,
        }).catch((err) => captureException(err, 'client_session_join'));
    }, [room, localIdentity, telemetryJwt, connectionState]);

    useEffect(() => {
        if (!room) return;
        const onDisc = () => {
            sessionJoinSentRef.current = null;
        };
        room.on(RoomEvent.Disconnected, onDisc);
        return () => {
            room.off(RoomEvent.Disconnected, onDisc);
        };
    }, [room]);

    const lastQualityAuditRef = useRef(0);
    useEffect(() => {
        if (!room?.localParticipant || !telemetryJwt) return;
        if (connectionState !== ConnectionState.Connected) return;
        const handler = () => {
            const now = Date.now();
            if (now - lastQualityAuditRef.current < 8000) return;
            lastQualityAuditRef.current = now;
            const body = buildClientAuditPayload(room, 'connection_quality_changed');
            if (!body) return;
            fetch(ENDPOINTS.CLIENT_PRESENCE, {
                method: 'POST',
                headers: authJsonHeaders(telemetryJwt),
                body: JSON.stringify(body),
                keepalive: true,
            }).catch(() => {});
        };
        room.on('connectionQualityChanged', handler);
        return () => {
            room.off('connectionQualityChanged', handler);
        };
    }, [room, telemetryJwt, connectionState]);

    useEffect(() => {
        if (!room) return;

        const handleTrackSubscribed = (track, pub, participant) => {
            if (track.source !== Track.Source.ScreenShare) return;

            const local = room.localParticipant;

            // 🔥 If I am already sharing and someone else starts → STOP MINE
            const localIsSharing = Array.from(local.tracks.values()).some(
                p => p.source === Track.Source.ScreenShare && p.track
            );

            if (localIsSharing && !participant.isLocal) {
                console.log('[override] stopping local screen share');
                local.setScreenShareEnabled(false);
            }
        };

        room.on('trackSubscribed', handleTrackSubscribed);
        return () => {
            room.off('trackSubscribed', handleTrackSubscribed);
        };
    }, [room]);

    useEffect(() => {
        if (!room) return;

        const handleActiveSpeakerChange = (speakers) => {
            if (speakers.length > 0) {
                setActiveSpeakerSid(speakers[0].sid);
            } else {
                setActiveSpeakerSid(null);
            }
        };

        room.on('activeSpeakersChanged', handleActiveSpeakerChange);
        return () => {
            room.off('activeSpeakersChanged', handleActiveSpeakerChange);
        };
    }, [room]);

    useEffect(() => {
        if (!room) return;

        const updateQuality = (participant) => {
            const level = connectionQualityToLevel(participant.connectionQuality);
            setNetworkQuality(prev => ({
                ...prev,
                [participant.sid]: level,
            }));
        };

        // Initial state from current participants
        room.participants.forEach(updateQuality);
        updateQuality(room.localParticipant);

        // Listen to room-level connection quality changes (livekit-client API)
        const handleConnectionQualityChanged = (quality, participant) => {
            const level = connectionQualityToLevel(quality ?? participant.connectionQuality);
            setNetworkQuality(prev => ({ ...prev, [participant.sid]: level }));
        };
        room.on('connectionQualityChanged', handleConnectionQualityChanged);
        room.on('participantConnected', updateQuality);
        room.on('participantDisconnected', (p) =>
            setNetworkQuality(prev => {
                const copy = { ...prev };
                delete copy[p.sid];
                return copy;
            })
        );

        return () => {
            room.off('connectionQualityChanged', handleConnectionQualityChanged);
            room.off('participantConnected', updateQuality);
            room.off('participantDisconnected', updateQuality);
        };
    }, [room]);


    const presenterName = screenTrack
        ? screenTrack.participant.identity
        : null;

    // 🔴 STAGE PRIORITY
    const stageTrack = screenTrack
        ? screenTrack
        : pinnedSid
            ? tracks.find(
                t =>
                    t.source === Track.Source.Camera &&
                    t.participant.sid === pinnedSid
            )
            : null;


    // 🔑 If stage is camera, remember whose camera it is
    const stageCameraSid =
        stageTrack?.source === Track.Source.Camera
            ? stageTrack.participant.sid
            : null;
    // Sidebar: exclude pinned camera on stage; remote presenter stays in strip (no stage PIP).
    const sidebarParticipantsWithTracks = remoteParticipantsWithTracks.filter(
        ({ participant }) => participant.sid !== stageCameraSid
    );

    useEffect(() => {
        if (!room) return;

        const handleData = (payload, participant, kind) => {
            if (kind !== DataPacket_Kind.RELIABLE) return;

            let msg;
            try {
                msg = JSON.parse(new TextDecoder().decode(payload));
            } catch {
                return;
            }

            // Recording status broadcast – update for all participants
            if (msg.type === 'recordingStatus' && typeof msg.recording === 'boolean') {
                setRecording(msg.recording);
                if (!msg.recording) {
                    setRecordingStarterIdentity(null);
                } else if (typeof msg.starterIdentity === 'string' && msg.starterIdentity) {
                    setRecordingStarterIdentity(msg.starterIdentity);
                }
                if (
                    msg.recordingFailure === true &&
                    typeof msg.message === 'string' &&
                    msg.message
                ) {
                    setRecordingStoppedMessage(msg.message);
                }
                return;
            }

            // Non-starter asked to stop. Prompt starter if this client is the starter.
            if (
                msg.type === 'recordingStopApprovalRequest' &&
                msg.roomName === room?.name &&
                msg.starterIdentity === room?.localParticipant?.identity
            ) {
                const requester = msg.requestedBy || participant?.identity || 'A participant';
                setPendingStopApproval({ requestId: msg.requestId, requestedBy: requester });
                return;
            }

            // Chat message (type 'chat' or legacy { text, time })
            if (msg.type === 'chat' || (msg.text != null && msg.time != null)) {
                setChatMessages(prev => [
                    ...prev,
                    {
                        from: participant?.identity ?? 'Unknown',
                        text: msg.text,
                        time: msg.time,
                    },
                ]);
                setUnreadCount(c => (chatOpen ? c : c + 1));
            }
        };

        room.on('dataReceived', handleData);
        return () => {
            room.off('dataReceived', handleData);
        };
    }, [room, chatOpen]);

    const sendChatMessage = async (text) => {
        const msg = {
            type: 'chat',
            text,
            time: new Date().toLocaleTimeString(),
        };

        await room.localParticipant.publishData(
            new TextEncoder().encode(JSON.stringify(msg)),
            DataPacket_Kind.RELIABLE
        );

        // local echo
        setChatMessages(prev => [
            ...prev,
            {
                from: room.localParticipant.identity,
                text,
                time: msg.time,
            },
        ]);
    };

    const handleStopApprovalDecision = async (approve) => {
        if (!pendingStopApproval || !room?.name || !room?.localParticipant?.identity) return;
        if (!telemetryJwt) {
            setRecordingStoppedMessage('Session token not ready; wait a moment and try again.');
            setPendingStopApproval(null);
            return;
        }
        setIsApprovalSubmitting(true);
        try {
            const res = await fetch(ENDPOINTS.RECORD_STOP_APPROVE, {
                method: 'POST',
                headers: authJsonHeaders(telemetryJwt),
                body: JSON.stringify({
                    roomName: room.name,
                    requestId: pendingStopApproval.requestId,
                    approvedBy: room.localParticipant.identity,
                    approve,
                }),
            });
            if (!res.ok) {
                setRecordingStoppedMessage('Could not process stop approval request.');
                return;
            }
            if (approve) {
                setRecording(false);
                setRecordingStoppedMessage('Recording has been stopped. Final recording will be available in a few moments.');
                try {
                    room.localParticipant.publishData(
                        new TextEncoder().encode(
                            JSON.stringify({
                                type: 'recordingStatus',
                                recording: false,
                                starterIdentity: null,
                            })
                        ),
                        DataPacket_Kind.RELIABLE
                    );
                } catch (_) {}
            } else {
                setRecordingStoppedMessage('Stop request denied. Recording continues.');
            }
        } catch (_) {
            setRecordingStoppedMessage('Could not process stop approval request.');
        } finally {
            setIsApprovalSubmitting(false);
            setPendingStopApproval(null);
        }
    };

    const local = room?.localParticipant;
    const localMicPub = local?.getTrack?.(Track.Source.Microphone);
    const localCamPub = local?.getTrack?.(Track.Source.Camera);
    const localVideoMuted = localCamPub
        ? (localCamPub.isMuted ?? !localCamPub.track)
        : true;

    // Interview-integrity monitor (TI-735 + TI-739b). Flag-gated (off by default);
    // fail-open — never disrupts the room. candidateId keys to room + participant.
    // Monitoring only runs after the candidate grants biometric consent (TI-738), and
    // TI-1099 restricts both the consent prompt and the monitoring to the CANDIDATE — the
    // recruiter is not the subject of the integrity check, so capturing their face and
    // recording a biometric consent decision for them is out of scope, not just noise.
    const isCandidate = participantRole === ROLE_CANDIDATE;
    const integrityCandidateId =
        room?.name && localIdentity && isCandidate ? `${room.name}:${localIdentity}` : null;
    const integrityState = useIntegrityMonitor({
        mediaStreamTrack: localCamPub?.track?.mediaStreamTrack,
        candidateId: integrityCandidateId,
        candidateName: localIdentity,
        jwt: integrityJwt, // TI-735: integrity-service token (NOT the telemetry JWT)
        enabled: INTEGRITY_UI_ENABLED && isCandidate && integrityConsent === true,
    });

    return (
        <div className={`room-container${screenTrack ? ' room-container--screen-share' : ''}`}>
            {recording && (
                <div className="recording-indicator" aria-live="polite">
                    Recording
                </div>
            )}

            {INTEGRITY_UI_ENABLED && integrityCandidateId && integrityConsent === null &&
                audioDisclosure !== null && (
                <IntegrityConsent
                    candidateId={integrityCandidateId}
                    jwt={telemetryJwt}
                    // The notice describes what THIS room actually does, so each clause is
                    // conditional on live config rather than hardcoded. `useIntegrityMonitor`
                    // enrolls a reference template and re-verifies on a ~10s cadence; it posts
                    // edge SIGNALS, not stored violation images, so evidenceKinds is empty here.
                    // Widening any of these changes the recorded notice version, which is what
                    // stops consent for a narrow scope being reused for a wider one.
                    // audioTranscription (TI-1111) is the SERVER observer flag, not a build
                    // flag — the render above waits for it so the scope is fixed when shown.
                    capabilities={{
                        identityVerification: true,
                        presenceMonitoring: true,
                        evidenceKinds: [],
                        audioTranscription: audioDisclosure,
                    }}
                    onDecision={setIntegrityConsent}
                />
            )}

            {integrityState.active && integrityState.message && (
                <div
                    className={`integrity-banner integrity-banner--${integrityState.level}`}
                    aria-live="polite"
                >
                    {integrityState.terminated
                        ? `Interview integrity check failed: ${integrityState.message}`
                        : integrityState.message}
                </div>
            )}

            {/* 🔊 AUDIO (ALWAYS RENDER, INVISIBLE) */}
            <RoomAudioRenderer />

            {screenTrack && (
                <div className="presenter-banner">
                    {presenterName} is presenting
                </div>
            )}

            <div className="content-row">
                <div className="main-content">
                    {stageTrack ? (
                        //  <div key="stage-layout" className="layout stage">
                        <ScreenShareView
                            key={
                                stageTrack.publication?.trackSid ||
                                stageTrack.participant?.sid ||
                                'stage'
                            }
                            screenTrack={stageTrack}
                            participantsWithTracks={sidebarParticipantsWithTracks}
                            onTileClick={setPinnedSid}
                            networkQuality={networkQuality}
                            activeSpeakerSid={activeSpeakerSid}
                            screenSharingSids={screenSharingSids}
                        />
                        //  </div>
                    ) : (
                        //  <div key="grid-layout" className="layout grid">
                        <VideoGrid
                            participantsWithTracks={remoteParticipantsWithTracks}
                            onTileClick={setPinnedSid}
                            networkQuality={networkQuality}
                            activeSpeakerSid={activeSpeakerSid}
                            screenSharingSids={screenSharingSids}
                        />
                        // </div>
                    )}
                    {chatOpen && (
                        <>
                            <button
                                type="button"
                                className="chat-backdrop"
                                aria-label="Close chat"
                                onClick={() => setChatOpen(false)}
                            />
                            <Chat
                                messages={chatMessages}
                                onSend={sendChatMessage}
                            />
                        </>
                    )}
                </div>
            </div>
            {/* 🔥 FLOATING SELF VIEW – shifts left when chat is open */}
            {/* Local self view: show even when camera is off (avatar) */}
            <div className={`floating-self-view${chatOpen ? ' floating-self-view--chat-open' : ''}${screenTrack ? ' floating-self-view--screen-active' : ''}`}>
                <TrackTile
                    track={localCameraTrack || null}
                    participant={room?.localParticipant}
                    isActive={room?.localParticipant?.sid === activeSpeakerSid}
                    networkLevel={room ? (networkQuality[room.localParticipant.sid] ?? 0) : 0}
                    isAudioMuted={localMicPub ? localMicPub.isMuted : true}
                    isVideoMuted={localVideoMuted}
                    isScreenSharing={local && screenSharingSids.has(local.sid)}
                    onClick={() =>
                        setPinnedSid(
                            pinnedSid === room?.localParticipant?.sid ? null : room?.localParticipant?.sid
                        )
                    }
                />
            </div>

            {recordingStoppedMessage && (
                <div className="recording-stopped-toast" role="status" aria-live="polite">
                    {recordingStoppedMessage}
                </div>
            )}

            {pendingStopApproval && (
                <div className="stop-approval-backdrop" role="dialog" aria-modal="true">
                    <div className="stop-approval-modal">
                        <h3>Stop Recording Request</h3>
                        <p>
                            <strong>{pendingStopApproval.requestedBy}</strong> requested to stop recording.
                            Do you want to approve?
                        </p>
                        <div className="stop-approval-actions">
                            <button
                                className="control-btn"
                                disabled={isApprovalSubmitting}
                                onClick={() => handleStopApprovalDecision(false)}
                            >
                                Keep Recording
                            </button>
                            <button
                                className="control-btn leave"
                                disabled={isApprovalSubmitting}
                                onClick={() => handleStopApprovalDecision(true)}
                            >
                                Approve Stop
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🎛️ CONTROLS – isRecordingActive so anyone can stop if starter dropped */}
            <Controls
                screenTrack={screenTrack}
                isRecordingActive={recording}
                recordingStarterIdentity={recordingStarterIdentity}
                audioSinkId={audioSinkId}
                onAudioSinkIdChange={setAudioSinkId}
                audioOutputDevices={audioOutputDevices}
                onToggleChat={() => setChatOpen(v => !v)}
                chatOpen={chatOpen}
                unreadCount={unreadCount}
                setUnreadCount={setUnreadCount}
                setChatOpen={setChatOpen}
                onStartRecording={() => {
                    setRecording(true);
                    setRecordingStarterIdentity(room?.localParticipant?.identity || null);
                }}
                onStopRecording={() => setRecording(false)}
                onRecordingStoppedMessage={setRecordingStoppedMessage}
            />
        </div>

    );
}
